// A component limitation's history — the slide-in over the Aircraft Item Details tab (2026-10-14).
// Read-only. GET /v6/items/{id}/limitation-history?limitation_id= (Aeronet.app
// app/items/limitations.py limitation_history_rows): every limitation_history row v6 and legacy
// wrote for the limitation, newest first: Add Item, Modify, Delete, Component Removal, CSV Import.
//
// Legacy (intent): the row's history popup, mod_aircraft/includes/aircraft_limitation_section_1.inc,
// and the aircraft-wide old -> new view aircraft_limitation_history.inc. A component's row repurposes
// limitation_history's columns (TSN at install in `document`, finite life in `version_url`, TSO at
// install in `life_install`); the API names them.
import React, { useEffect, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";

import { axios } from "../../../api";
import CustomFilterPanelPremium from "../../../components/CustomFilterPanelPremium";
import Sheet, { SheetContent, SheetHeader } from "../../../components/Sheet";
import { commonDataGridPremiumSx } from "../../../utils/dataGridSx";
import { formatTenantDateTime, useTenantDateFormat } from "../../../utils/tenantDate";

export interface LimitationHistoryRow {
  id: number;
  limitation_id: number | null;
  history_type: string | null;
  completed: string | null;
  completed_by: string | null;
  based_on: string | null;
  limitation_type: string | null;
  period: string | null;
  finite_life: string | null;
  tsn: string | null;
  tso: number | string | null;
  next_due: string | null;
  remaining: string | null;
  warning: number | string | null;
  warning_unit: string | null;
  extension: number | string | null;
  extension_reason: string | null;
  position: string | null;
  reference: string | null;
  notes: string | null;
  log_reference: string | null;
  serial_number: string | null;
}

const num = (v: unknown) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? String(Number(n.toFixed(2))) : String(v);
};

export function limitationHistoryColumns(datePattern: string | undefined): GridColDef<LimitationHistoryRow>[] {
  return [
    {
      field: "completed",
      headerName: "When",
      flex: 1,
      valueFormatter: (value: string | null) => formatTenantDateTime(value, datePattern),
    },
    { field: "completed_by", headerName: "By", flex: 0.9 },
    { field: "history_type", headerName: "Change", flex: 0.9 },
    { field: "based_on", headerName: "Based On", flex: 0.9 },
    { field: "limitation_type", headerName: "Type", flex: 0.8 },
    { field: "period", headerName: "Period", flex: 0.6, valueFormatter: (v) => num(v) },
    { field: "finite_life", headerName: "Finite Life", flex: 0.6, valueFormatter: (v) => num(v) },
    { field: "tsn", headerName: "@Install TSN", flex: 0.6, valueFormatter: (v) => (v === "UNK" ? "UNK" : num(v)) },
    { field: "tso", headerName: "@Install TSO", flex: 0.6, valueFormatter: (v) => num(v) },
    { field: "next_due", headerName: "Due", flex: 0.7 },
    {
      field: "warning",
      headerName: "Warning",
      flex: 0.7,
      valueGetter: (_v, row) => `${num(row.warning)} ${row.warning_unit ?? ""}`.trim(),
    },
    { field: "extension", headerName: "Extension", flex: 0.6, valueFormatter: (v) => (Number(v) ? num(v) : "") },
    { field: "position", headerName: "Position", flex: 0.6 },
    {
      field: "notes",
      headerName: "Notes",
      flex: 1.4,
      valueGetter: (_v, row) => [row.log_reference, row.notes].filter(Boolean).join(" — "),
    },
  ];
}

interface Props {
  itemId: number;
  limitationId: number;
  title: string;
  onClose: () => void;
}

const LimitationHistorySheet: React.FC<Props> = ({ itemId, limitationId, title, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const datePattern = useTenantDateFormat();
  const [rows, setRows] = useState<LimitationHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const columns = useMemo(() => limitationHistoryColumns(datePattern), [datePattern]);

  useEffect(() => {
    let current = true;
    setLoading(true);
    axios
      .get(`/v6/items/${itemId}/limitation-history`, { params: { limitation_id: limitationId }, cache: false })
      .then((resp) => current && setRows(Array.isArray(resp.data) ? resp.data : []))
      .catch(() => current && enqueueSnackbar("Error fetching limitation history", { variant: "error" }))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [enqueueSnackbar, itemId, limitationId]);

  return (
    <Sheet open onClose={onClose} size="wide" data-testid="limitation-history-sheet">
      <SheetHeader onClose={onClose} title={title} />
      <SheetContent>
        {!loading && rows.length === 0 ? (
          <Typography variant="body2" sx={{ mb: 1 }}>
            No history recorded for this limitation.
          </Typography>
        ) : null}
        <DataGridPremium
          slots={{ filterPanel: CustomFilterPanelPremium }}
          autoHeight
          density="compact"
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          hideFooter={rows.length <= 15}
          sx={{ ...commonDataGridPremiumSx, borderLeft: "none", borderRight: "none" }}
          aria-label="limitation-history"
        />
      </SheetContent>
    </Sheet>
  );
};

export default LimitationHistorySheet;
